"use client";

import { useEffect, useRef, useState, type CSSProperties } from "react";
import Image from "next/image";
import { motion, useInView, useReducedMotion } from "framer-motion";
import {
  ArrowUp,
  BookOpen,
  Brain,
  ChevronRight,
  Database,
  Mic,
  MousePointer2,
  Plus,
  Sparkles,
  Target,
  ThumbsUp,
  TriangleAlert,
} from "lucide-react";
import styles from "./product-demos.module.scss";

export type WorkflowMoment = "morning" | "live" | "review";
export const WORKFLOW_MOMENTS: WorkflowMoment[] = ["morning", "live", "review"];
type SceneKind = WorkflowMoment | "companion";
type TimedFrame = Keyframe & { at: number };
const EASE = "cubic-bezier(0.16, 1, 0.3, 1)";
const PLAYBACK_RATES = { morning: 1.5, live: 1.4, review: 1.65, companion: 1.3 };
const LIVE = {
  duration: 10500,
  hover: [2400, 6200],
  dismiss: [3000, 6800],
  restore: [9000, 8000],
};
const REVIEW = {
  duration: 15600,
  tabs: [14000, 2600, 6400, 11000],
  slide: 740,
};
const MORNING = {
  duration: 12600,
  chart: 2700,
  scroll: 1500,
  strategies: 5100,
  feedback: 9450,
  back: 11100,
};
const PHASES = {
  morning: [
    [0, "score"],
    [MORNING.chart, "chart"],
    [MORNING.strategies, "strategies"],
    [MORNING.feedback, "feedback"],
  ],
  live: [
    [0, "alerts"],
    [LIVE.hover[0], "keep-first"],
    [LIVE.dismiss[0], "dismiss-first"],
    [LIVE.hover[1], "keep-next"],
    [LIVE.dismiss[1], "dismiss-next"],
    [LIVE.restore[1], "restore"],
  ],
  review: [
    [0, "summary"],
    [REVIEW.tabs[1], "daily-pnl"],
    [REVIEW.tabs[2], "trend"],
    [REVIEW.tabs[3], "when"],
    [REVIEW.tabs[0], "summary"],
  ],
  companion: [
    [0, "thinking"],
    [200, "response"],
    [1000, "details"],
    [6000, "complete"],
  ],
} as const;

function useDemoTimeline(kind: SceneKind, duration: number, replayKey = "") {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    root.dataset.sceneState = reduced ? "complete" : "paused";
    root.dataset.sceneReduced = String(!!reduced);
    if (reduced) {
      root.dataset.sceneStep =
        kind === "companion" ? "complete" : PHASES[kind][0][1];
      return;
    }
    const players: Animation[] = [];
    const playbackRate = PLAYBACK_RATES[kind];
    const loop = kind !== "companion";
    const track = (element: Element, frames: TimedFrame[], once = false) => {
      const keys = frames.map(({ at, ...frame }) => ({
        ...frame,
        offset: Math.min(1, at / duration),
      }));
      if (keys[0].offset !== 0) keys.unshift({ ...keys[0], offset: 0 });
      if (keys[keys.length - 1].offset !== 1)
        keys.push({ ...keys[keys.length - 1], offset: 1 });
      const player = element.animate(keys, {
        duration,
        iterations: loop && !once ? Infinity : 1,
        fill: "both",
      });
      player.playbackRate = playbackRate;
      player.pause();
      players.push(player);
      return player;
    };
    const reveal = (
      element: Element,
      at: number,
      length = 650,
      motion = "reveal",
      once = false,
      hide = 0,
    ) => {
      const from =
        motion === "ring"
          ? { clipPath: "inset(100% 0 0 0)" }
          : motion === "line"
            ? { strokeDashoffset: "100" }
            : motion === "bar"
              ? { transform: "scaleY(0)" }
              : {
                  opacity: 0,
                  transform: "translateY(8px)",
                  ...(motion === "word" && { filter: "blur(5px)" }),
                };
      const to =
        motion === "ring"
          ? { clipPath: "inset(0% 0 0 0)" }
          : motion === "line"
            ? { strokeDashoffset: "0" }
            : motion === "bar"
              ? { transform: "scaleY(1)" }
              : {
                  opacity: 1,
                  transform: "translateY(0)",
                  ...(motion === "word" && { filter: "blur(0)" }),
                };
      track(
        element,
        [
          ...(hide
            ? [
                { at: 0, ...to },
                { at: hide, ...to },
                { at: hide + 1, ...from },
              ]
            : []),
          { at, ...from, easing: EASE },
          { at: at + length, ...to },
        ],
        once,
      );
    };
    root
      .querySelectorAll<HTMLElement | SVGElement>("[data-scene-at]")
      .forEach((element) => {
        reveal(
          element,
          Number(element.dataset.sceneAt),
          Number(element.dataset.sceneLength) || 650,
          element.dataset.sceneMotion,
          "sceneOnce" in element.dataset,
          Number(element.dataset.sceneHide) || 0,
        );
      });
    const counts = [
      ...root.querySelectorAll<HTMLElement>("[data-scene-count]"),
    ].map((element) => ({
      element,
      value: Number(element.dataset.sceneCount),
      at: Number(element.dataset.countAt || 150),
      length: Number(element.dataset.countLength || 1400),
      once: "sceneOnce" in element.dataset,
    }));
    const showPanel = (
      selector: string,
      windows: [number, number][],
      transition = {
        enter: 450,
        exit: 350,
        overlap: false,
        move: false,
        settle: 0,
      },
    ) => {
      root.querySelectorAll(selector).forEach((element) => {
        const frames: TimedFrame[] = [
          {
            at: 0,
            opacity: windows[0][0] === 0 ? 1 : 0,
            visibility: windows[0][0] === 0 ? "visible" : "hidden",
            ...(transition.move && {
              transform:
                windows[0][0] === 0 ? "translateY(0)" : "translateY(10px)",
            }),
          },
        ];
        windows.forEach(([start, end]) => {
          if (start)
            frames.push(
              {
                at: start,
                opacity: 0,
                visibility: "visible",
                easing: EASE,
                ...(transition.move && { transform: "translateY(10px)" }),
              },
              {
                at: start + transition.enter,
                opacity: 1,
                visibility: "visible",
                easing: EASE,
                ...(transition.move && { transform: "translateY(2px)" }),
              },
            );
          if (start && transition.move && transition.settle)
            frames.push({
              at: start + transition.enter + transition.settle,
              opacity: 1,
              visibility: "visible",
              transform: "translateY(0)",
            });
          if (end < duration) {
            const fadeAt = transition.overlap
              ? end
              : Math.max(start, end - transition.exit);
            frames.push({
              at: fadeAt,
              opacity: 1,
              visibility: "visible",
              easing: "cubic-bezier(.4,0,1,1)",
              ...(transition.move && { transform: "translateY(0)" }),
            });
            if (transition.move)
              frames.push(
                {
                  at: fadeAt + transition.exit,
                  opacity: 0,
                  visibility: "visible",
                  easing: EASE,
                  transform: "translateY(8px)",
                },
                {
                  at: fadeAt + transition.exit + transition.settle,
                  opacity: 0,
                  visibility: "hidden",
                  transform: "translateY(10px)",
                },
              );
            else
              frames.push({
                at: fadeAt + transition.exit,
                opacity: 0,
                visibility: "hidden",
              });
          }
        });
        track(element, frames);
      });
    };
    if (kind === "morning") {
      const scroll = root.querySelector("[data-morning-scroll]");
      if (scroll)
        track(scroll, [
          { at: 0, transform: "translateY(0)" },
          {
            at: MORNING.chart,
            transform: "translateY(0)",
            easing: "cubic-bezier(.65,0,.35,1)",
          },
          {
            at: MORNING.chart + MORNING.scroll,
            transform: "translateY(-32.875em)",
          },
          {
            at: MORNING.back,
            transform: "translateY(-32.875em)",
            easing: "cubic-bezier(.65,0,.35,1)",
          },
          { at: MORNING.back + MORNING.scroll, transform: "translateY(0)" },
        ]);
      showPanel('[data-morning-panel="feedback"]', [
        [0, MORNING.strategies],
        [MORNING.feedback, duration],
      ], { enter: 525, exit: 525, overlap: true, move: true, settle: 150 });
      showPanel(
        '[data-morning-panel="strategies"]',
        [[MORNING.strategies, MORNING.feedback]],
        { enter: 525, exit: 525, overlap: true, move: true, settle: 150 },
      );
    }
    if (kind === "live") {
      root
        .querySelectorAll<HTMLElement>("[data-dismiss-at]")
        .forEach((element) => {
          const at = Number(element.dataset.dismissAt),
            back = Number(element.dataset.restoreAt),
            height =
              element.getBoundingClientRect().height /
              parseFloat(getComputedStyle(element).fontSize);
          track(element, [
            {
              at: 0,
              height: height + "em",
              opacity: 1,
              marginBottom: "0.65em",
            },
            {
              at,
              height: height + "em",
              opacity: 1,
              marginBottom: "0.65em",
              easing: "cubic-bezier(.65,0,.35,1)",
            },
            { at: at + 600, height: "0px", opacity: 0, marginBottom: "0em" },
            {
              at: back,
              height: "0px",
              opacity: 0,
              marginBottom: "0em",
              easing: EASE,
            },
            {
              at: back + 700,
              height: height + "em",
              opacity: 1,
              marginBottom: "0.65em",
            },
          ]);
        });
      root.querySelectorAll("[data-keep]").forEach((element, index) => {
        const at = LIVE.hover[index];
        const idle = { backgroundColor: "#0b0b0b", color: "#999" };
        track(element, [
          { at: 0, ...idle },
          { at, ...idle },
          { at: at + 250, backgroundColor: "#313131", color: "#fff" },
          { at: LIVE.dismiss[index] + 600, backgroundColor: "#313131", color: "#fff" },
          { at: LIVE.dismiss[index] + 601, ...idle },
        ]);
      });
    }
    if (kind === "review") {
      const [summary, daily, trend, when] = REVIEW.tabs;
      const fade = {
        enter: 500,
        exit: 450,
        overlap: true,
        move: false,
        settle: 0,
      };
      showPanel(
        '[data-review-panel="summary"]',
        [
          [0, daily],
          [summary, duration],
        ],
        fade,
      );
      showPanel('[data-review-panel="daily-pnl"]', [[daily, trend]], fade);
      showPanel('[data-review-panel="trend"]', [[trend, when]], fade);
      showPanel('[data-review-panel="when"]', [[when, summary]], fade);
      const underline = root.querySelector("[data-review-underline]");
      const tabs = root.querySelectorAll<HTMLElement>("[data-review-tab]");
      if (underline && tabs.length) {
        const size = parseFloat(getComputedStyle(underline).fontSize);
        const place = (i: number) => ({
          transform: `translateX(${tabs[i].offsetLeft / size}em)`,
          width: tabs[i].offsetWidth / size + "em",
        });
        const frames: TimedFrame[] = [{ at: 0, ...place(0) }];
        [1, 2, 3, 0].forEach((i) => {
          const at = REVIEW.tabs[i];
          frames.push(
            { at, ...place(i ? i - 1 : 3), easing: "cubic-bezier(.65,0,.35,1)" },
            { at: at + REVIEW.slide, ...place(i) },
          );
        });
        track(underline, frames);
      }
    }
    const cursor = root.querySelector<HTMLElement>("[data-scene-cursor]");
    if (cursor) {
      const parent = cursor.parentElement!,
        bounds = parent.getBoundingClientRect();
      const fontSize = parseFloat(getComputedStyle(cursor).fontSize);
      const point = (element: Element, subtractY = 0) => {
        const rect = element.getBoundingClientRect();
        return `translate(${(rect.left - bounds.left + rect.width / 2) / fontSize}em,${(rect.top - bounds.top + rect.height / 2) / fontSize - subtractY}em)`;
      };
      const idle = "translate(29em,21em)";
      let poses: [number, string][] = [];
      if (kind === "morning") {
        const tabs = root.querySelectorAll("[data-morning-tab]");
        const feedback = point(tabs[0], 32.875),
          strategies = point(tabs[1], 32.875);
        poses = [
          [0, idle],
          [MORNING.chart, idle],
          [MORNING.chart + MORNING.scroll, strategies],
          [MORNING.strategies, strategies],
          [MORNING.feedback - 1050, strategies],
          [MORNING.feedback, feedback],
          [MORNING.feedback + 900, feedback],
          [MORNING.back + MORNING.scroll, idle],
        ];
      } else if (kind === "live") {
        const buttons = root.querySelectorAll("[data-keep]");
        const firstRow = root.querySelector<HTMLElement>("[data-dismiss-at]")!;
        const removed =
          firstRow.getBoundingClientRect().height / fontSize + 0.65;
        const first = point(buttons[0]),
          next = point(buttons[1], removed);
        poses = [
          [0, idle],
          [LIVE.hover[0] - 1000, idle],
          [LIVE.hover[0], first],
          [LIVE.dismiss[0], first],
          [LIVE.dismiss[0] + 900, idle],
          [LIVE.hover[1] - 1000, idle],
          [LIVE.hover[1], next],
          [LIVE.dismiss[1], next],
          [LIVE.dismiss[1] + 900, idle],
        ];
      } else {
        const tabs = root.querySelectorAll("[data-review-tab]");
        const [summary, ...rest] = REVIEW.tabs;
        poses = [[0, idle]];
        [...rest, summary].forEach((at, i) => {
          const tab = point(tabs[(i + 1) % 4]);
          poses.push([at - 1150, poses[poses.length - 1][1]], [at, tab]);
        });
        poses.push([duration, idle]);
      }
      track(
        cursor,
        poses.map(([at, transform]) => ({
          at,
          transform,
          easing: "cubic-bezier(.65,0,.35,1)",
        })),
      );
      const clicks =
        kind === "morning"
          ? [MORNING.strategies, MORNING.feedback]
          : kind === "live"
            ? LIVE.dismiss.map((at) => at - 100)
            : [...REVIEW.tabs].sort((a, b) => a - b);
      const clickTarget = cursor.querySelector("svg") || cursor;
      const frames: TimedFrame[] = [{ at: 0, transform: "scale(1)" }];
      clicks.forEach((at) =>
        frames.push(
          { at: at - 70, transform: "scale(1)" },
          { at, transform: "scale(.82)" },
          { at: at + 220, transform: "scale(1)" },
        ),
      );
      track(clickTarget, frames);
    }
    const clock = track(root, [
      { at: 0, outlineOffset: "0px" },
      { at: duration, outlineOffset: "0px" },
    ]);
    let inView = false,
      frame = 0,
      wakeTimer = 0;
    const cancelSample = () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(wakeTimer);
    };
    const sample = () => {
      const elapsed = Number(clock.currentTime) || 0,
        time = loop ? elapsed % duration : Math.min(elapsed, duration);
      const step =
        [...PHASES[kind]].reverse().find(([at]) => time >= at)?.[1] ||
        PHASES[kind][0][1];
      if (root.dataset.sceneStep !== step) {
        root.dataset.sceneStep = step;
        root.dataset.sceneTime = String(Math.round(time));
      }
      let counting = false;
      let nextWake = duration - time;
      counts.forEach(({ element, value, at, length, once }) => {
        const progress = Math.max(
          0,
          Math.min(1, ((once ? elapsed : time) - at) / length),
        );
        const text = String(Math.round(value * (1 - (1 - progress) ** 3)));
        if (element.textContent !== text) element.textContent = text;
        if (progress > 0 && progress < 1) counting = true;
        else if (!progress) nextWake = Math.min(nextWake, at - time);
      });
      if (inView && !document.hidden && clock.playState === "running") {
        if (counting) frame = requestAnimationFrame(sample);
        else {
          const nextPhase = PHASES[kind].find(([at]) => at > time);
          if (nextPhase) nextWake = Math.min(nextWake, nextPhase[0] - time);
          // WAAPI owns the motion; JavaScript sleeps between numeric/phase changes.
          wakeTimer = window.setTimeout(
            sample,
            Math.max(1, nextWake / playbackRate),
          );
        }
      }
    };
    const sync = () => {
      cancelSample();
      const awake = inView && !document.hidden;
      players.forEach((player) => {
        if (player.playState !== "finished")
          awake ? player.play() : player.pause();
      });
      root.dataset.sceneState =
        clock.playState === "finished"
          ? "complete"
          : awake
            ? "playing"
            : "paused";
      const elapsed = Number(clock.currentTime) || 0;
      root.dataset.sceneTime = String(
        Math.round(loop ? elapsed % duration : Math.min(elapsed, duration)),
      );
      sample();
    };
    const observer = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
        sync();
      },
      { threshold: 0.28 },
    );
    observer.observe(root);
    document.addEventListener("visibilitychange", sync);
    const seek = (event: Event) => {
      const detail = (event as CustomEvent<{ time: number; paused?: boolean }>)
        .detail;
      const time = Math.max(
        0,
        Math.min(duration - 1, Number(detail?.time) || 0),
      );
      cancelSample();
      players.forEach((player) => {
        player.currentTime = time;
        if (detail?.paused) player.pause();
      });
      if (detail?.paused) {
        root.dataset.sceneState = "paused";
        root.dataset.sceneTime = String(Math.round(time));
        sample();
      } else sync();
    };
    root.addEventListener("demo:seek", seek);
    clock.onfinish = sync;
    return () => {
      cancelSample();
      observer.disconnect();
      document.removeEventListener("visibilitychange", sync);
      root.removeEventListener("demo:seek", seek);
      players.forEach((player) => player.cancel());
      counts.forEach(({ element }) => {
        element.textContent = element.dataset.sceneCount || "";
      });
    };
  }, [kind, duration, reduced, replayKey]);
  return ref;
}

function CoachMark() {
  return (
    <span className={styles.coachMark}>
      <Image
        src="/ai-features/fox-1.svg"
        width={36}
        height={36}
        alt=""
        loading="eager"
      />
    </span>
  );
}
function CoachHeader() {
  return (
    <div className={styles.coachHeader}>
      <CoachMark />
      <strong>TS Coach</strong>
      <span className={styles.beta}>Beta</span>
      <span className={styles.powered}>Powered by your trading data.</span>
    </div>
  );
}
function DemoCursor() {
  return (
    <span className={styles.demoCursor} data-scene-cursor aria-hidden="true">
      <MousePointer2
        className={styles.demoCursorIcon}
        fill="white"
        stroke="#111"
        strokeWidth={1.1}
      />
    </span>
  );
}
function Score({ compact = false }: { compact?: boolean }) {
  return compact ? (
    <div className={styles.compactScore}>
      <strong data-scene-count="67" data-count-at="2200">
        67
      </strong>
      <span>Good</span>
    </div>
  ) : (
    <div className={styles.score}>
      <div className={styles.scoreTrack}>
        <Image
          src="/ai-features/hero-panels/workflow-score-base.svg"
          width={129}
          height={173.33}
          alt=""
        />
      </div>
      <div
        className={styles.scoreReveal}
        data-scene-at="150"
        data-scene-motion="ring"
        data-scene-length="1400"
        data-scene-once
      >
        <div className={styles.scoreFill}>
          <Image
            src="/ai-features/hero-panels/workflow-score-fill.svg"
            width={128.183}
            height={86.6649}
            alt=""
          />
        </div>
      </div>
      <i className={styles.scoreNeedle} />
      <strong
        className={styles.scoreValue}
        data-scene-count="67"
        data-scene-once
      >
        67
      </strong>
      <span className={styles.scoreLabel}>Good</span>
      <small className={styles.scoreMin}>00</small>
      <small className={styles.scoreMax}>100</small>
    </div>
  );
}
const METRICS = [
  { label: "Body", value: 45, icon: "body" },
  { label: "Mind", value: 78, icon: "mind" },
  { label: "Moment", value: 54, icon: "moment" },
  { label: "Preparation", value: 33, icon: "preparation" },
];
function Metrics({ compact = false }: { compact?: boolean }) {
  return (
    <div className={compact ? styles.compactMetrics : styles.metrics}>
      {METRICS.map(({ label, value, icon }, i) => (
        <div key={label}>
          {!compact && (
            <Image
              src={`/ai-features/hero-panels/workflow-metric-${icon}.svg`}
              width={35}
              height={35}
              alt=""
            />
          )}
          <span>
            <strong
              data-scene-count={value}
              data-count-at={(compact ? 2300 : 200) + i * 120}
              data-count-length="900"
              data-scene-once={compact ? undefined : ""}
            >
              {value}
            </strong>
            <small>{label}</small>
          </span>
        </div>
      ))}
    </div>
  );
}
const TREND_PATH =
  "M0 143.6L9 126.8L31 93.2L46.1 137.2L58.1 94.5L74.1 112.6L94.1 98.4L109.1 1.3L119.1 56.9L126.2 44L131.2 66L139.2 62.1L157.2 106.1L170.2 84.1L190.2 80.2L206.3 98.4L208.3 88L222.3 89.3L237.3 78.9L253.3 81.5L269.3 56.9L280.3 50.5L283.4 29.8L298.4 58.2L307.4 45.3L317.4 47.9L326.4 41.4L332.4 34.9L348.4 7.8L355.4 32.4L373.5 22L381.5 40.1L387.5 25.9L399.5 19.4L407.5 24.6L413.5 18.1L424.5 16.8L430.5 0L444.6 16.8L451.6 90.6L455.6 18.1L465.6 34.9L484.6 31.1L506.6 24.6L510.6 12.9L523.7 16.8L529.7 27.2L532.7 34.9L544.7 47.9L551.7 32.4L562.7 36.2L569.7 19.4L588.7 28.5L595.7 62.1L603.8 36.2";
function TradingLine({
  at = MORNING.chart,
  marker = false,
}: {
  at?: number;
  marker?: boolean;
}) {
  return (
    <div className={styles.tradingLine}>
      <div className={styles.chartAxis}>
        {[
          "$ 3.500",
          "$ 3.000",
          "$ 2.500",
          "$ 2.000",
          "$ 1.500",
          "$ 1.000",
          "$ 500",
          "$ 0",
        ].map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
      <svg viewBox="0 0 604 175" preserveAspectRatio="none">
        <path
          d={TREND_PATH + "L604 175H0Z"}
          fill="currentColor"
          fillOpacity=".08"
          {...(marker && {
            "data-scene-at": at + 600,
            "data-scene-length": 1300,
          })}
        />
        <path
          d={TREND_PATH}
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          pathLength="100"
          strokeDasharray="100"
          data-scene-at={at}
          data-scene-motion="line"
          data-scene-length="1600"
        />
      </svg>
      {marker && (
        <div className={styles.lineMarker} data-scene-at={at + 1200}>
          <i />
          <span>
            May 10, 2025 · 9:13 PM
            <br />
            <small>Net P&amp;L:</small> +US$ 3.233,50
          </span>
        </div>
      )}
      <div className={styles.chartDays}>
        {[
          "May 7",
          "May 8",
          "May 9",
          "May 10",
          "May 11",
          "May 12",
          "May 13",
        ].map((day) => (
          <span key={day}>{day}</span>
        ))}
      </div>
    </div>
  );
}
export function MorningDemo() {
  const ref = useDemoTimeline("morning", MORNING.duration);
  return (
    <div
      ref={ref}
      className={styles.workflowScene + " " + styles.morningScene}
      data-demo-scene="morning"
    >
      <div className={styles.productWindow + " " + styles.morningWindow}>
        <div className={styles.morningScroll} data-morning-scroll>
          <div className={styles.morningOverview}>
            <div className={styles.person}>
              <span>
                <Image
                  src="/ai-features/hero-panels/workflow-person-bull.svg"
                  width={18}
                  height={18}
                  alt=""
                />
              </span>
              Felipe Terra
            </div>
            <div className={styles.morningTitle}>
              <strong>Your morning brief is ready!</strong>
              <span>Here is your daily score</span>
            </div>
            <Score />
            <Metrics />
          </div>
          <div className={styles.morningChart}>
            <div className={styles.chartBands}>
              <Brain />
              <BookOpen />
            </div>
            <TradingLine />
          </div>
          <div className={styles.morningTabs}>
            <span data-morning-tab="feedback">Feedback</span>
            <span data-morning-tab="strategies">strategys</span>
          </div>
          <div className={styles.morningPanels}>
            <div data-morning-panel="feedback">
              <div className={styles.wins}>
                <strong>
                  <ThumbsUp />
                  Nice things
                </strong>
                <p>Held under daily loss limit 6 of 7 days</p>
                <p>Breakouts hit 68% — your strongest setup</p>
              </div>
              <div className={styles.losses}>
                <strong>
                  <TriangleAlert />
                  Things to pay attention
                </strong>
                <p>Every trade after 14:30 was a loss — −$95</p>
                <p>3 revenge trades after losses — −$140</p>
              </div>
            </div>
            <div data-morning-panel="strategies">
              <strong>Entry rules</strong>
              <p>
                <b>1</b>Speed of Tape (Zwingend für VIX unter 20)
                <small>Required Rule</small>
              </p>
              <p>
                <b>2</b>85% Imbalance
              </p>
              <p>
                <b>3</b>85% Imbalance
              </p>
            </div>
          </div>
        </div>
        <DemoCursor />
      </div>
    </div>
  );
}
const ALERTS = [
  {
    Icon: Target,
    title: "you have reached a max drawdown limit",
    action: true,
    highlighted: true,
  },
  { Icon: TriangleAlert, title: "There was a breach in the rules" },
  {
    Icon: Database,
    title: "Your weekly update is done, check it out!",
    highlighted: true,
    review: true,
  },
  { Icon: TriangleAlert, title: "There was a breach in the rules" },
  {
    Icon: Target,
    title: "you have reached a max drawdown limit",
    action: true,
  },
  { Icon: TriangleAlert, title: "There was a breach in the rules" },
];
export function LiveDemo() {
  const ref = useDemoTimeline("live", LIVE.duration);
  return (
    <div
      ref={ref}
      className={styles.workflowScene + " " + styles.liveScene}
      data-demo-scene="live"
    >
      <div className={styles.productWindow + " " + styles.liveWindow}>
        <div className={styles.alertList}>
          {ALERTS.map(({ Icon, title, action, highlighted, review }, i) => (
            <div
              className={styles.alertWrap}
              key={i}
              data-dismiss-at={action ? LIVE.dismiss[i ? 1 : 0] : undefined}
              data-restore-at={action ? LIVE.restore[i ? 1 : 0] : undefined}
            >
              <div
                className={styles.alert}
                data-highlighted={highlighted}
                data-scene-at={i * 200}
                data-scene-length="700"
                data-scene-once
              >
                {highlighted && <i className={styles.alertDot} />}
                <span className={styles.alertIcon}>
                  <Icon />
                </span>
                <div>
                  <strong>{title}</strong>
                  <span>1h ago</span>
                  {action && (
                    <div className={styles.drawdownActions}>
                      <span>Change drawdown</span>
                      <span data-keep>Keep this way</span>
                    </div>
                  )}
                </div>
                {review && <ChevronRight />}
              </div>
            </div>
          ))}
        </div>
        <DemoCursor />
      </div>
    </div>
  );
}
const WINS = [
  "Best session +$842 Tuesday — every trade in focus",
  "Held under daily loss limit 6 of 7 days",
  "Breakouts hit 68% — your strongest setup",
];
const LOSSES = [
  "Thursday news-trades — 4 trades, −$180",
  "Every trade after 14:30 was a loss — −$95",
  "3 revenge trades after losses — −$140",
];
const HEATMAP = ["GORGRRR", "GRRGGGR", "GOGOOGR", "RGGRRGR", "ORGOROO"];
export function ReviewDemo() {
  const ref = useDemoTimeline("review", REVIEW.duration);
  return (
    <div
      ref={ref}
      className={styles.workflowScene + " " + styles.reviewScene}
      data-demo-scene="review"
    >
      <div className={styles.productWindow + " " + styles.reviewWindow}>
        <div className={styles.windowHeading}>
          <strong>Weekly review</strong>
          <small>Complete all steps to save</small>
        </div>
        <div className={styles.reviewSummary}>
          <strong>
            <Sparkles />
            Strong discipline week
          </strong>
          <div>
            {[
              ["TRADES", "22"],
              ["WIN RATE", "64%"],
              ["BEST DAY", "+$842"],
              ["WORST DAY", "−$253"],
              ["BRIEFED", "5 / 7"],
              ["IN-FOCUS", "64%"],
            ].map(([label, value]) => (
              <span key={label}>
                {label}
                <b
                  className={
                    label === "BEST DAY"
                      ? styles.positive
                      : label === "WORST DAY"
                        ? styles.negative
                        : ""
                  }
                >
                  {value}
                </b>
              </span>
            ))}
          </div>
        </div>
        <div className={styles.reviewTabs}>
          {["Summary", "Daily P&L", "Trend", "When"].map((tab, i) => (
            <span
              key={tab}
              data-review-tab={["summary", "daily-pnl", "trend", "when"][i]}
            >
              {tab}
            </span>
          ))}
          <i data-review-underline />
        </div>
        <div className={styles.reviewPanels}>
          <div className={styles.reviewColumns} data-review-panel="summary">
            <div className={styles.wins}>
              <strong>
                <ThumbsUp />
                What went well
              </strong>
              {WINS.map((text, i) => (
                <p
                  key={text}
                  data-scene-at={REVIEW.tabs[0] + 300 + i * 150}
                  data-scene-hide={REVIEW.tabs[1] + 600}
                >
                  {text}
                </p>
              ))}
            </div>
            <div className={styles.losses}>
              <strong>
                <TriangleAlert />
                What needs work
              </strong>
              {LOSSES.map((text, i) => (
                <p
                  key={text}
                  data-scene-at={REVIEW.tabs[0] + 450 + i * 150}
                  data-scene-hide={REVIEW.tabs[1] + 600}
                >
                  {text}
                </p>
              ))}
            </div>
          </div>
          <div className={styles.reviewDaily} data-review-panel="daily-pnl">
            <div className={styles.barPlot} data-bar-chart="daily-pnl">
              {[32, 84, 54, -35, 45].map((value, i) => (
                <div className={styles.reviewDay} key={i}>
                  <i
                    className={
                      value < 0 ? styles.reviewLoss : styles.reviewGain
                    }
                    style={
                      { "--day-bar": Math.abs(value) + "%" } as CSSProperties
                    }
                    data-bar-value={value}
                    data-scene-at={REVIEW.tabs[1] + 300 + i * 70}
                    data-scene-motion="bar"
                    data-scene-length="700"
                  />
                  <span>{["Mon", "Tue", "Wed", "Thu", "Fri"][i]}</span>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.reviewTrend} data-review-panel="trend">
            <TradingLine at={REVIEW.tabs[2] + 300} marker />
          </div>
          <div className={styles.reviewWhen} data-review-panel="when">
            <span>Net P&L by day and hour, greener is better</span>
            <div className={styles.heatHours}>
              {["09", "10", "11", "12", "13", "14", "15"].map((hour) => (
                <span key={hour}>{hour}</span>
              ))}
            </div>
            {HEATMAP.map((row, r) => (
              <div className={styles.heatRow} key={r}>
                <span>{["Mon", "Tue", "Wed", "Thu", "Fri"][r]}</span>
                {[...row].map((color, c) => (
                  <i
                    key={c}
                    data-heat={color}
                    data-scene-at={REVIEW.tabs[3] + 300 + (r * 7 + c) * 20}
                    data-scene-length="350"
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className={styles.reviewFocus}>
          <Target />
          <span>Focus for next week</span>
        </div>
        <DemoCursor />
      </div>
    </div>
  );
}
function ChatInput() {
  return (
    <div className={styles.chatInput}>
      <Plus />
      <span>Ask TS Coach about trading</span>
      <Mic />
      <i>
        <ArrowUp />
      </i>
    </div>
  );
}
function ResponseWords({ children }: { children: string }) {
  return (
    <strong className={styles.companionTitle}>
      {children.split(" ").map((word, i) => (
        <span
          key={i}
          data-scene-at={200 + i * 55}
          data-scene-motion="word"
          data-scene-length="450"
        >
          {word}{" "}
        </span>
      ))}
    </strong>
  );
}
export function CompanionDemo({ moment }: { moment: WorkflowMoment }) {
  const [shown, setShown] = useState(moment);
  const [exiting, setExiting] = useState(false);
  const [visible, setVisible] = useState(true);
  const latest = useRef(moment);
  latest.current = moment;
  const ref = useDemoTimeline("companion", 6500, shown);
  const inView = useInView(ref, { amount: 0.28 });
  const reduced = useReducedMotion();
  const awake = inView && visible && !reduced;

  useEffect(() => {
    const update = () => setVisible(!document.hidden);
    update();
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  useEffect(() => {
    // Retain the shown response through its exit; rapid scroll picks the latest target.
    if (!awake) {
      setShown(moment);
      setExiting(false);
    } else setExiting(moment !== shown);
  }, [moment, shown, awake]);

  return (
    <div
      ref={ref}
      className={styles.companionScene}
      data-demo-scene="companion"
      data-companion-moment={shown}
      data-companion-target={moment}
      data-companion-transition={exiting ? "exiting" : "entering"}
    >
      <CoachHeader />
      <div className={styles.companionThread}>
        <motion.div
          key={shown}
          className={styles.companionMessage}
          initial={awake ? { opacity: 0, y: 6 } : false}
          animate={{ opacity: exiting ? 0 : 1, y: exiting ? -6 : 0 }}
          transition={{
            duration: awake ? (exiting ? 0.3 : 0.48) : 0,
            ease: exiting ? [0.4, 0, 1, 1] : [0.16, 1, 0.3, 1],
          }}
          onAnimationComplete={(definition) => {
            // A previous entrance can finish after the next exit has started.
            if (
              exiting &&
              typeof definition === "object" &&
              "opacity" in definition &&
              definition.opacity === 0 &&
              latest.current !== shown
            ) {
              setShown(latest.current);
              setExiting(false);
            }
          }}
        >
          <div className={styles.messageAuthor} data-scene-at="0">
            <CoachMark />
            <span>TS COACH</span>
          </div>
          <div className={styles.companionBody}>
            {shown === "morning" ? (
              <>
                <ResponseWords>Your morning brief is ready!</ResponseWords>
                <div className={styles.scoreCard} data-scene-at="1000">
                  <span>Here is your daily score</span>
                  <Score compact />
                  <Metrics compact />
                </div>
              </>
            ) : shown === "live" ? (
              <>
                <ResponseWords>
                  you have reached a max drawdown limit
                </ResponseWords>
                <div className={styles.companionActions} data-scene-at="1000">
                  <span>Change drawdown</span>
                  <span>Keep this way</span>
                </div>
              </>
            ) : (
              <>
                <ResponseWords>Here’s what your week shows.</ResponseWords>
                <div className={styles.coachAlert} data-scene-at="1000">
                  <Sparkles />
                  <strong>Strong discipline week</strong>
                  <p>22 trades at a 64% win rate.</p>
                  <span className={styles.positive}>Best day +$842</span>
                </div>
                <p className={styles.companionInsight} data-scene-at="3200">
                  Your review is ready. See what went well, what needs work and
                  where to focus next.
                </p>
              </>
            )}
          </div>
        </motion.div>
      </div>
      <div className={styles.companionFooter}>
        <ChatInput />
        <p>
          TS Coach can make mistakes. Always verify important information before
          acting on it.
        </p>
      </div>
    </div>
  );
}

const QUESTION = "Why do I keep losing on Thursdays?";
export function GenericChatDemo() {
  return (
    <div className={styles.genericChat} data-demo-loop>
      <div className={styles.genericHeader}>
        <span>
          {[0, 1, 2].map((i) => (
            <Image
              key={i}
              src="/ai-features/hero-panels/comparison-window-dot.svg"
              width={10}
              height={10}
              alt=""
            />
          ))}
        </span>
        Generic AI chat
      </div>
      <div className={styles.comparisonConversation}>
        <div
          className={styles.userMessage}
          data-demo-at="250"
          data-demo-duration="450"
        >
          <small>You</small>
          <span>{QUESTION}</span>
        </div>
        <div
          className={styles.genericAnswer}
          data-demo-at="800"
          data-demo-duration="700"
        >
          Losing streaks are common in trading. Consider reviewing your risk
          management, staying disciplined and avoiding emotional decisions. Many
          traders find it helpful to keep a journal.
        </div>
        <span
          className={styles.noAccess}
          data-demo-at="1800"
          data-demo-duration="450"
        >
          No access to your trades
        </span>
      </div>
    </div>
  );
}

const FINDINGS = [
  ["Thursday news-trades", "4 trades, −$180"],
  ["Every trade after 14:30 was a loss", "−$95"],
  ["3 revenge trades after losses", "−$140"],
  ["Held under daily loss limit", "6 of 7 days"],
];
// Native Figma bar heights, including the shorter Tuesday bar.
const DAY_BARS = [102.4328, 159.9636, 49.8132, 136.8109, 98.2232, 8.4191];
const HOUR_BARS = [49.9269, 91.4155, -48.5205, 47.1142, 105.4794, 56.9589];
function PerformanceChart({ hourly = false }: { hourly?: boolean }) {
  const values = hourly ? HOUR_BARS : DAY_BARS;
  return (
    <div className={styles.performanceChart} data-hourly={hourly}>
      <div className={styles.chartGlow}>
        <div>
          <Image
            src={`/ai-features/hero-panels/comparison-chart-${hourly ? "hour" : "day"}-glow.svg`}
            width={hourly ? 210.834 : 210.354}
            height={hourly ? 499.697 : 498.559}
            alt=""
          />
        </div>
      </div>
      <div className={styles.chartHeading}>
        <span>Performance {hourly ? "by hour" : "by Day"}</span>
        <div className={styles.chartTools}>
          {!hourly && (
            <span>
              <Image
                src="/ai-features/hero-panels/comparison-chart-bars.svg"
                width={13}
                height={13}
                alt=""
              />
              <Image
                src="/ai-features/hero-panels/comparison-chart-line.svg"
                width={13}
                height={13}
                alt=""
              />
            </span>
          )}
          <span>
            <b>$</b>
            <i>%</i>
          </span>
        </div>
      </div>
      <div className={styles.performanceAxis}>
        {[
          "4.000",
          "3.500",
          "3.000",
          "2.500",
          "2.000",
          "1.500",
          "1.000",
          "500",
        ].map((amount) => (
          <span key={amount}>US$ {amount}</span>
        ))}
      </div>
      <div className={styles.chartPlot}>
        <div
          className={styles.barPlot}
          data-bar-chart={hourly ? "hour" : "day"}
          data-bar-count={values.length}
        >
          {values.map((value, i) => (
            <div key={i} className={styles.chartColumn}>
              <div
                className={value < 0 ? styles.lossBar : styles.gainBar}
                style={
                  {
                    "--bar-height": `${Math.abs(value) / 8}rem`,
                  } as CSSProperties
                }
                data-bar-value={value}
                data-demo-at={2550 + i * 90}
                data-demo-duration="950"
                data-demo-motion="bar"
              />
              <span>
                {hourly
                  ? ["4:00", "5:00", "6:00", "7:00", "8:00", "9:00"][i]
                  : [
                      "Sunday",
                      "Monday",
                      "Tuesday",
                      "Wednesday",
                      "Friday",
                      "Saturday",
                    ][i]}
              </span>
            </div>
          ))}
        </div>
      </div>
      {!hourly && (
        <div
          className={styles.performanceTooltip}
          data-demo-at="3350"
          data-demo-duration="450"
        >
          <strong>Wednesday</strong>
          <span>
            Net P&amp;L: <b>+US$ 3.233,50</b>
          </span>
        </div>
      )}
    </div>
  );
}
// The mobile Figma draws the chart wider than the receipts column, cropping
// its toggles and tooltip, so the row is zoomed out until the first chart fits.
function useFitFirstChart() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const charts = ref.current;
    const box = charts?.parentElement;
    if (!charts || !box) return;
    let width = -1;
    const fit = () => {
      // Width only: the zoom changes the box height and would re-trigger this.
      const next = Math.floor(box.getBoundingClientRect().width);
      if (next === width) return;
      width = next;
      const chart = charts.firstElementChild as HTMLElement | null;
      if (!chart) return;
      charts.style.removeProperty("zoom");
      const scale = Math.min(1, width / chart.getBoundingClientRect().width);
      if (scale < 1) charts.style.setProperty("zoom", String(scale));
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(box);
    return () => observer.disconnect();
  }, []);
  return ref;
}

export function CoachChatDemo() {
  const chartsRef = useFitFirstChart();
  return (
    <div className={styles.coachChat} data-demo-loop>
      <CoachHeader />
      <div className={styles.comparisonConversation}>
        <div
          className={styles.userMessage}
          data-demo-at="250"
          data-demo-duration="450"
        >
          <small>You</small>
          <span>{QUESTION}</span>
        </div>
        <strong
          className={styles.analysisTitle}
          data-demo-at="700"
          data-demo-duration="450"
        >
          Here’s what your week shows.
        </strong>
        <div className={styles.findings}>
          {FINDINGS.map(([text, value], i) => (
            <div
              key={text}
              data-demo-at={1200 + i * 220}
              data-demo-duration="500"
            >
              <i aria-hidden="true">
                <span />
                <span />
                <span />
              </i>
              <span>{text}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
        <div
          className={styles.receipts}
          data-demo-at="2350"
          data-demo-duration="500"
        >
          <div className={styles.basedOn}>Based on</div>
          <div ref={chartsRef} className={styles.charts}>
            <PerformanceChart />
            <PerformanceChart hourly />
          </div>
        </div>
      </div>
    </div>
  );
}
